<template>
  <div
    :class="
      $b({
        fullWidth: fullWidth && !singleLineSwatches,
        display,
        wrap,
        size,
        singleLine: singleLineSwatches,
        'background-style': backgroundStyle,
        shape: mappedChoices[0]?.option?.partType === 'strap' && size !== 'small' ? 'rectangle' : shape
      })
    "
  >
    <div
      v-for="choice in mappedChoices"
      :key="choice.option.sku"
      :class="
        $b('item', {
          circle: choice.option.partType !== 'strap' || size === 'small',
          rectangle: choice.option.partType === 'strap' && size !== 'small',
          isActive: choice.isActive,
        })
      "
    >
      <VisionSwatch
        v-if="choice.option.partType !== 'strap' || size === 'small'"
        :swatches="[choice.option.swatch]"
        :is-active="choice.isActive"
        :disabled="
          (!choice.option.isOnPreorder && !choice.option.isInStock) || disabled
        "
        :overlay-text="
          choice.option.isOnPreorder &&
          !choice.option.isInStock &&
          !choice.option.preorderAsDelayedDespatch
            ? 'Pre-Order'
            : null
        "
        :badge-text="choice.option.isLimitedEdition ? 'LE' : ''"
        :full-width="true"
        :icon-overlay="lensIcon(choice)"
        :class="$b('choices')"
        @select="$emit('select', choice.option)"
      />
      <button
        v-else
        :class="$b('choice')"
        :disabled="(!choice.option.isOnPreorder && !choice.option.isInStock) || disabled"
        :style="choice.option.swatch.styles"
        @click="$emit('select', choice.option)"
      >
        <img :src="buildStrapImageUrl(choice)" />
        <span v-if="choice.option.isLimitedEdition" :class="$b('limited-edition-badge')">
          LE
        </span>
      </button>
    </div>
    <slot />
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'

export default {
  name: 'ChoiceSelect',

  props: {
    choices: { type: Array, required: true },
    part: { type: Object, required: true },
    customisedProduct: { type: CustomisedProduct, required: true },
    shape: {
      type: String,
      default: 'square',
      validator: (value) => {
        return ['square', 'circle'].includes(value)
      },
      required: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    display: {
      type: String,
      default: 'grid',
      validator: (value) => {
        return ['grid', 'flex'].includes(value)
      },
    },
    size: {
      type: String,
      default: 'default',
      validator: (value) => {
        return ['default', 'small'].includes(value)
      },
    },
    wrap: {
      type: Boolean,
      default: false,
    },
    disableInfiniteSwatchIcon: {
      type: Boolean,
      default: false,
    },
    singleLineSwatches: { type: Boolean, default: false },
    disabled: {
      type: Boolean,
      default: false,
    },
    backgroundStyle: {
      type: String,
      default: 'grey',
      validator: (value) => {
        return ['grey', 'transparent'].includes(value)
      },
    },
  },

  emits: ['select'],

  computed: {
    mappedChoices() {
      const activeOption =
        this.customisedProduct.allChoices[this.part.stockManagedSKUPrefix]
      return this.choices.map((option) => {
        return {
          option,
          isActive: option.sku === activeOption?.sku,
        }
      })
    },
  },

  methods: {
    lensIcon(choice) {
      if (choice.option.isIrisLens) {
        return 'ir'
      } else if (choice.option.isWaterLens) {
        return 'water'
      } else if (choice.option.isCat4Lens) {
        return 'cat4'
      }
      return ''
    },
    buildStrapImageUrl(choice) {
      if(!choice.option.swatch?.strapImage?.src) return ''

      const url = new URL(choice.option.swatch.strapImage?.src)

      url.searchParams.append('rect', '130,65,459,166')

      return url
    }
  },
}
</script>

<style lang="postcss">
.ChoiceSelect {
  @apply grid grid-cols-7 gap-xxs
  py-4 rounded-lg;
  font-size: 0;
  max-width: 441px;

  &--display-flex {
    @apply flex flex-nowrap gap-xxxs;

    &.ChoiceSelect--size-small {
      @apply gap-1.5;
    }
  }

  &--wrap {
    @apply flex-wrap;
  }

  &--full-width {
    @apply w-full;
  }

  &--single-line {
    @apply min-w-full overflow-x-auto flex items-start
      px-xxs scrollbar-none;
  }

  @screen sm {
    @apply grid-cols-7;
  }

  @screen md {
    @apply grid-cols-6;
  }

  @screen lg {
    @apply grid-cols-7;
  }
}

.ChoiceSelect__item--circle {
  @apply relative pb-[100%] max-w-[50px];

  @supports (aspect-ratio: 1/1) {
    @apply aspect-square pb-0;
  }

  .ChoiceSelect--display-flex & {
    @apply w-[24px] md:w-[32px] flex-shrink-0;
  }

  .ChoiceSelect--single-line & {
    @apply w-[42px] flex-shrink-0;
  }

  .ChoiceSelect--size-small & {
    @apply w-[20px];
  }
}

.ChoiceSelect--shape-rectangle {
  @apply grid-cols-4;

  .ChoiceSelect__item--rectangle {
    @apply relative bg-white border border-grey-300 p-1 rounded aspect-[98/42];

    &:hover {
      @apply border-grey-500;
    }

    &.ChoiceSelect__item--is-active {
        @apply border-grey-500;
    }

    .ChoiceSelect__choice {
      @apply w-full h-full rounded-sm overflow-hidden;

      &:disabled {
        @apply cursor-not-allowed;

        img {
          @apply opacity-50;
        }
      }

      img {
        @apply w-full h-full object-contain;
      }
    }

    .ChoiceSelect__limited-edition-badge {
      @apply absolute top-1/2 z-[2] -right-2 transform -translate-y-1/2 h-[20px] w-[20px]
      bg-gradient rounded-full border-2
      text-[9px] leading-none font-semibold flex items-center justify-center text-white;
      border-color: var(--vision-swatch-limited-edition-border-color, theme('colors.white'));
    }
  }
}

.ChoiceSelect__icon--iris {
  @apply w-5 h-5;
}

.ChoiceSelect__icon--recycled {
  @apply w-5 h-5;
}

/*
  Modifiers

  --background-style-transparent
*/
.ChoiceSelect--background-style-transparent {
  @apply p-0 pb-2 bg-transparent;

  &.ChoiceSelect--size-small {
    @apply pb-0;
  }
}
</style>
